/**
 * @file use-order-queue-polling.ts
 * @module app/modules/orders-delivery/hooks/use-order-queue-polling
 * @description Shared polling hook for `ordersDelivery.checkStatus`. Consolidates the
 * repeated "poll every 1s until order-queue record resolves" pattern duplicated across
 * add-new-order(-assist), edit-order-details, goods-loading-details, and review-orders-list.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.1
 * @version 1.3.1
 */

import { useEffect } from 'react';
import APICallService from '../../../../api/apiCallService';
import { ordersDelivery } from '../../../../api/apiEndPoints';
import {
  OrderQueueFailed,
  OrderQueueProcessed,
  OrdersDelivery,
} from '../../../../utils/constants';
import { error } from '../../../../Global/toast';

interface UseOrderQueuePollingParams {
  isPolling: boolean;
  orderId: string;
  warehouseId?: string | null;
  onFailed: (response: any) => void;
  onProcessed: (response: any) => void;
  pollIntervalMs?: number;
}

/**
 * Polls the order-queue processing status (order create/edit) until it resolves
 * to `OrderQueueFailed` or `OrderQueueProcessed`, then invokes the matching callback.
 * Polling stops safely on unmount or when `isPolling` flips false (via effect cleanup).
 */
export const useOrderQueuePolling = ({
  isPolling,
  orderId,
  warehouseId,
  onFailed,
  onProcessed,
  pollIntervalMs = 1000,
}: UseOrderQueuePollingParams) => {
  useEffect(() => {
    if (!isPolling) return;
    let isCancelled = false;

    const checkOrderStatus = async () => {
      let apiService = new APICallService(
        ordersDelivery.checkStatus,
        orderId,
        '',
        '',
        '',
        '',
        OrdersDelivery,
        warehouseId || null
      );
      let response = await apiService.callAPI();
      if (isCancelled) return;

      if (response) {
        if (response?.orderQueueRecord?.status === OrderQueueFailed) {
          error(response.error);
          onFailed(response);
        } else if (
          response?.orderQueueRecord?.status === OrderQueueProcessed
        ) {
          onProcessed(response);
        } else {
          setTimeout(checkOrderStatus, pollIntervalMs);
        }
      } else {
        error(response?.error);
        onFailed(response);
      }
    };

    checkOrderStatus();

    return () => {
      isCancelled = true;
    };
  }, [isPolling]);
};
